// Picking a default icon for a location that has no photo yet.
//
// A keyword list, which is the opposite of the conclusion reached about search — worth saying
// why, because it looks like the same mistake. Search fails if it does not know a word: the
// space of things people might type is unbounded, so a hand-written vocabulary is always
// incomplete in a way the user notices. Here the space of OUTPUTS is what is bounded: there
// are a dozen or so icons, an unmatched name gets the neutral box, and nobody is worse off
// than they were when every location got the neutral box. Completeness is not the goal;
// recognising "Kitchen" is.
//
// The name is all there is to go on. Locations have no type field, and the eventual plan is
// to collapse spaces, locations and items into one node kind, which would remove even that.

import { normalizeText, foldPlural } from "./text.js";

// icon key -> the words that pick it. Keys are matched against the app's icon set in app.jsx.
const KEYWORDS = {
  house: ["house", "home", "apartment", "flat", "cottage", "cabin"],
  bed: ["bed", "bedroom", "bedside", "nightstand", "dresser", "guestroom", "nursery"],
  kitchen: ["kitchen", "pantry", "larder", "cupboard", "cutlery", "silverware", "utensil", "dish"],
  fridge: ["fridge", "refrigerator", "freezer", "icebox", "cooler"],
  bath: ["bathroom", "bath", "shower", "washroom", "toilet", "ensuite", "vanity", "medicine"],
  sofa: ["living", "lounge", "loungeroom", "den", "family", "parlour", "parlor", "sofa", "couch"],
  car: ["garage", "car", "van", "truck", "trunk", "boot", "vehicle", "carport"],
  shed: ["shed", "garden", "greenhouse", "allotment", "yard", "potting", "outhouse"],
  tools: ["workshop", "toolbox", "tool", "workbench", "hardware", "utility", "boiler"],
  tent: ["camping", "camp", "tent", "outdoor", "hiking", "backpacking"],
  laundry: ["laundry", "washing", "washer", "dryer", "linen", "airing"],
  desk: ["office", "study", "desk", "studio", "workroom"],
  wardrobe: ["closet", "wardrobe", "clothes", "clothing", "coat", "dressing"],
  books: ["shelf", "shelves", "bookshelf", "bookcase", "library", "book", "media"],
  drawer: ["drawer", "cabinet", "cupboards", "sideboard", "chest", "filing"],
  boxes: ["storage", "attic", "loft", "basement", "cellar", "garret", "crate", "bin", "tub", "container", "locker"],
};

// word -> icon key, built once. Plural-folded on both sides so "shelves" and "shelf" land in
// the same place as they do in search.
const BY_WORD = new Map();
for (const [icon, words] of Object.entries(KEYWORDS)) {
  for (const word of words) BY_WORD.set(foldPlural(word), icon);
}

export const FALLBACK_ICON = "box";

// The icon key for a location name, or FALLBACK_ICON.
//
// Read RIGHT TO LEFT, because English puts the container last: "kitchen drawer" is a drawer
// that happens to be in the kitchen, and "garage shelf" is a shelf. Matching the first word
// instead would label both by the room they are in and lose the distinction that matters.
export function iconKeyForLocation(name) {
  const words = normalizeText(name).split(" ").filter(Boolean);
  for (let i = words.length - 1; i >= 0; i -= 1) {
    const hit = BY_WORD.get(foldPlural(words[i]));
    if (hit) return hit;
  }
  return FALLBACK_ICON;
}
